"use client";

import React, { createContext, useContext } from "react";
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import type { BaseEntity, EntityHooks } from "../types";

/**
 * The per-row services a table subscribes to ONCE, not once per row.
 *
 * MUTATIONS. `TableRowActionsWrapper` and `RowContextMenuContent` each called
 * `hooks.useUpdate()` + `hooks.useDelete()`, and both render once per row. With
 * ~25 visible rows plus `CRM_VIRTUAL_OVERSCAN` (10) that was ~140 React Query
 * mutation-cache subscriptions for a single rendered table. Neither hook takes
 * a per-row argument — `useUpdate: () => { mutateAsync: (data: {id}) => … }`,
 * the row id travels with `mutateAsync`, not with the hook call — so hoisting
 * is behaviour-preserving. `isPending` is the only other member and no row-path
 * consumer reads it (`useTableRowActionContext` uses `mutateAsync` only).
 *
 * ROLE FILTER. `useActionFiltering()` ran once per row too (in
 * `RowContextMenuContentView` and again in `useTableRowActions`), and it pulls
 * `useRoleCheck` + `useSequencesAccess` — two more context reads and a memo
 * each time. It takes NO argument and its `filterActionsByRole` is memoized on
 * `[roleName, canUseSequences]` only, both of which are user-level, not
 * row-level; no row-path consumer reads a per-row-varying field from it. So it
 * is hoistable for the same reason the mutations are, and it rides the same
 * context so a row pays for ONE `useContext`, not two.
 *
 * The provider is mounted ONLY when `hooks` is present, so `hooks.useUpdate()`
 * here is unconditional. If `hooks` ever went from absent to present the
 * provider MOUNTS (a remount), which is safe; calling the hooks conditionally
 * inside one component would not be.
 */
export type RowActionRoleFilter = ReturnType<typeof useActionFiltering>["filterActionsByRole"];

export interface SimpleTableRowServices<TData extends BaseEntity> {
  update: ReturnType<EntityHooks<TData>["useUpdate"]>;
  delete: ReturnType<EntityHooks<TData>["useDelete"]>;
  filterActionsByRole: RowActionRoleFilter;
}

const SimpleTableRowServicesContext = createContext<
  SimpleTableRowServices<BaseEntity> | undefined
>(undefined);

interface ProviderProps<TData extends BaseEntity> {
  hooks: EntityHooks<TData>;
  children: React.ReactNode;
}

export function SimpleTableRowServicesProvider<TData extends BaseEntity>({
  hooks,
  children,
}: ProviderProps<TData>) {
  const update = hooks.useUpdate();
  const del = hooks.useDelete();
  const { filterActionsByRole } = useActionFiltering();

  const value = React.useMemo<SimpleTableRowServices<TData>>(
    () => ({ update, delete: del, filterActionsByRole }),
    [update, del, filterActionsByRole]
  );

  return (
    <SimpleTableRowServicesContext.Provider
      // Stored at the BaseEntity boundary so one context serves every entity
      // type; each consumer re-narrows via useSimpleTableRowServices<TData>().
      value={value as unknown as SimpleTableRowServices<BaseEntity>}
    >
      {children}
    </SimpleTableRowServicesContext.Provider>
  );
}

/**
 * The table-level row services, or `undefined` outside a table (the row-action
 * components are also used standalone on cards, drilldowns and detail panels —
 * see ENTITY_TABLE_ROW_ACTIONS — where they keep calling the hooks themselves).
 */
export function useSimpleTableRowServices<TData extends BaseEntity = BaseEntity>():
  | SimpleTableRowServices<TData>
  | undefined {
  const context = useContext(SimpleTableRowServicesContext);
  return context as unknown as SimpleTableRowServices<TData> | undefined;
}
